import { isPlatformBrowser } from '@angular/common';
import { DestroyRef, inject, Injectable, PLATFORM_ID, signal } from '@angular/core';
import { SwUpdate, VersionEvent } from '@angular/service-worker';
import { BrowserWindowService } from './browser-window.service';

/**
 * Контракт сообщений межвкладочной координации (собственный неймспейс).
 * Используется канал BroadcastChannel 'wh:sw-update'.
 */
type WhSwUpdateMsg =
  | { type: 'force-reload' }                                              // запрос жёсткой перезагрузки во всех вкладках
  | { type: 'version-ready'; current?: string | null; latest?: string | null } // сообщение, что новая версия готова (с хэшами версий, если есть)
  | { type: 'check-now' };                                                // запрос выполнить проверку обновления

/**
 * UpdateService
 * -----------------------------------------------------------------------------
 * Назначение:
 *  - Следит за обновлениями Angular Service Worker (PWA).
 *  - Умеет работать идемпотентно (многократные вызовы startUpdateCheck() безопасны).
 *  - Периодические проверки + проверка при активации вкладки (visibilitychange).
 *  - Координация между вкладками через BroadcastChannel (неймспейс wh:sw-update).
 *  - Безопасная перезагрузка: ждём смены контроллера (controllerchange), чтобы не попасть
 *    в смешанное состояние кэшей (белые экраны, разъехавшиеся стили).
 *  - Сигналы для UI: isUpdating, updateAvailable, currentVersion, latestVersion.
 *  - Автоматическое обновление с коротким системным overlay вместо "молчаливого рывка".
 *  - Подробное логирование на русском, управляемое флагом debug.
 *
 * Использование:
 *  1) В AppComponent (или корневом bootstrap-файле) вызвать:
 *     constructor(private update: UpdateService) {
 *       this.update.setDebug(!environment.production); // включить логи в dev
 *       this.update.startUpdateCheck();
 *     }
 *
 *  2) Для ручной проверки добавить кнопку/меню:
 *     (click)="update.manualCheck()"
 *
 *  3) Для жёсткого обновления из UI:
 *     (click)="update.confirmUpdateNow()"
 */
@Injectable({ providedIn: 'root' })
export class UpdateService {
  // --- DI / платформенные защиты ------------------------------------------------
  private readonly platformId = inject(PLATFORM_ID);
  private readonly swUpdate = inject(SwUpdate, { optional: true });
  private readonly destroyRef = inject(DestroyRef);
  private readonly browserWindow = inject(BrowserWindowService);

  // --- Сигналы состояния для UI -------------------------------------------------
  /** Показывать индикатор (спиннер/тост) во время активации новой версии и переключения контроллера. */
  readonly isUpdating = signal(false);

  /** Полноэкранный overlay, который объясняет пользователю, что приложение само обновляется. */
  readonly overlayVisible = signal(false);
  readonly overlayTitleKey = signal('app-update.title');
  readonly overlayTextKey = signal('app-update.text');

  /** Флаг, что новая версия скачана и готова к активации (можно подсветить кнопку "Обновить"). */
  readonly updateAvailable = signal(false);

  /** Текущая/новая версии (хэши из Angular SW, если доступны). */
  readonly currentVersion = signal<string | null>(null);
  readonly latestVersion = signal<string | null>(null);

  // --- Настройки (в миллисекундах) ----------------------------------------------
  private readonly config = {
    checkIntervalMs: 60 * 60 * 1000,  // периодическая проверка: 1 час
    minSpinnerMs: 1000,            // минимальная длительность отображения индикатора
    forceDelayMs: 0,               // доп. задержка (если нужна оркестрация)
    normalRouteDelayMs: 400,       // короткая пауза, чтобы пользователь увидел системное сообщение
    criticalRouteDelayMs: 1500,    // чуть более мягкая пауза на рабочих inventory-экранах
    crossTabReloadDelayMs: 350,    // короткий показ overlay, если другая вкладка уже обновилась
    guardReloadStormMs: 5 * 60 * 1000,   // защита от "шторма" перезагрузок: 5 минут
  } as const;

  // --- Внутреннее состояние -----------------------------------------------------
  private updateCheckIntervalId: number | null = null;
  private started = false; // защита от повторного запуска
  private autoUpdateScheduled = false;
  private pendingAutoUpdateTimeoutId: number | null = null;

  // Межвкладочная координация: неймспейс канала и ключи хранилища
  private static readonly CHANNEL = 'wh:sw-update';
  private static readonly LS_RELOAD_ONCE = 'wh:sw:reload_once_at';

  private readonly channel = this.hasBrowser
    ? new BroadcastChannel(UpdateService.CHANNEL)
    : undefined;

  private readonly criticalRoutePrefixes = [
    '/app/inventory-review',
    '/app/inventory/detail',
    '/app/inventory-locations',
    '/app/scanner',
    '/app/catalog/product',
  ] as const;

  // SSR-помощник
  private get hasBrowser(): boolean {
    return isPlatformBrowser(this.platformId);
  }

  // --- Логирование (русские сообщения) -----------------------------------------
  /** Переключатель подробных логов (можно активировать из кода приложения). */
  private debug = false;
  public setDebug(enabled: boolean): void {
    this.debug = enabled;
    this.log('режим отладки:', enabled);
  }

  private log(...args: unknown[]) { if (this.debug) console.log('[UpdateService]', ...args); }
  private warn(...args: unknown[]) { if (this.debug) console.warn('[UpdateService]', ...args); }
  private error(...args: unknown[]) { if (this.debug) console.error('[UpdateService]', ...args); }

  // -----------------------------------------------------------------------------
  // Публичный API
  // -----------------------------------------------------------------------------

  public canCheckForUpdates(): boolean {
    return this.hasBrowser && !!this.swUpdate && this.swUpdate.isEnabled;
  }

  /**
   * Запуск слежения за обновлениями. Можно вызывать многократно — метод идемпотентен.
   * Рекомендуется вызвать один раз при старте приложения.
   */
  public startUpdateCheck(): void {
    const swUpdate = this.swUpdate;

    // Защиты: SSR/без браузера/без SW/в Dev-сборке без enable — просто выходим.
    if (!this.hasBrowser || !swUpdate || !swUpdate.isEnabled) {
      this.log('запуск пропущен (browser:', this.hasBrowser, 'swUpdate:', !!swUpdate, 'enabled:', swUpdate?.isEnabled, ')');
      return;
    }
    if (this.started) { this.log('уже запущен — пропускаю'); return; }
    this.started = true;

    this.log('startUpdateCheck(): подписываемся на события SW');

    // 1) События жизненного цикла версий от Angular SW
    swUpdate.versionUpdates.subscribe((event: VersionEvent) => {
      this.log('versionUpdates:', event.type, event);
      switch (event.type) {
        case 'VERSION_DETECTED':
          this.log('обнаружена новая версия:', event.version?.hash);
          break;

        case 'VERSION_INSTALLATION_FAILED':
          this.error('ошибка установки новой версии:', event.error);
          break;

        case 'VERSION_READY':
          // Новая версия скачана и готова к активации
          this.currentVersion.set(event.currentVersion?.hash ?? null);
          this.latestVersion.set(event.latestVersion?.hash ?? null);
          this.updateAvailable.set(true);

          // Сообщим другим вкладкам, что новая версия готова (для синхронизации UX)
          this.postToOthers({ type: 'version-ready', current: this.currentVersion(), latest: this.latestVersion() });

          this.log('новая версия готова — запускаем автообновление');
          this.scheduleAutoUpdate();
          break;

        default:
          // Другие типы нам не критичны
          break;
      }
    });

    // 2) Неисправимая ошибка SW — перезагрузка с предохранителем от шторма
    swUpdate.unrecoverable.subscribe(e => {
      this.warn('неисправимая ошибка Service Worker:', e.reason);

      const now = Date.now();
      try {
        const prev = Number(localStorage.getItem(UpdateService.LS_RELOAD_ONCE) || '0');
        if (!prev || now - prev > this.config.guardReloadStormMs) {
          localStorage.setItem(UpdateService.LS_RELOAD_ONCE, String(now));
          this.reloadHard();
        } else {
          this.warn('перезагрузка подавлена защитой от шторма (слишком часто)');
        }
      } catch {
        this.warn('localStorage недоступен — выполняем перезагрузку');
        this.reloadHard();
      }
    });

    // 3) Безопасная перезагрузка: ждём, пока новый SW станет контроллером страницы
    navigator.serviceWorker?.addEventListener('controllerchange', () => {
      this.log('controllerchange получен; isUpdating=', this.isUpdating());
      if (this.isUpdating()) {
        this.reloadHard();
      }
    });

    // 4) Первая явная проверка обновления
    this.safeCheckForUpdate();

    // 5) Периодические проверки
    if (!this.updateCheckIntervalId && this.config.checkIntervalMs > 0) {
      this.updateCheckIntervalId = window.setInterval(
        () => this.safeCheckForUpdate(),
        this.config.checkIntervalMs
      );
      this.log('запущен интервал проверки каждые', this.config.checkIntervalMs, 'мс');
    }

    // 6) Дешевая проверка при активации вкладки (экономит батарею и сеть)
    document.addEventListener('visibilitychange', this.onVisibilityChange, { passive: true });

    // 7) Межвкладочная координация
    this.channel?.addEventListener('message', (ev: MessageEvent<WhSwUpdateMsg>) => {
      this.log('получено межвкладочное сообщение:', ev.data);
      switch (ev.data?.type) {
        case 'force-reload':
          this.log('другая вкладка запросила перезагрузку — показываем overlay и обновляемся');
          this.handleCrossTabReload();
          break;

        case 'version-ready':
          // Если другая вкладка уже увидела новую версию — синхронизируем состояние
          this.currentVersion.set(ev.data.current ?? null);
          this.latestVersion.set(ev.data.latest ?? null);
          this.updateAvailable.set(true);
          this.scheduleAutoUpdate();
          break;

        case 'check-now':
          this.safeCheckForUpdate();
          break;
      }
    });

    // 8) Очистка ресурсов на случай нестандартных сценариев (микрофронты и т.п.)
    this.destroyRef.onDestroy(() => this.stop());
  }

  /**
   * Остановить периодические проверки и слушатели (полезно в микрофронтендах/виджетах).
   */
  public stop(): void {
    if (!this.hasBrowser) return;
    if (this.updateCheckIntervalId !== null) {
      clearInterval(this.updateCheckIntervalId);
      this.updateCheckIntervalId = null;
      this.log('интервал проверки остановлен');
    }
    document.removeEventListener('visibilitychange', this.onVisibilityChange as any);
    this.channel?.close();
    this.clearPendingAutoUpdate();
    this.started = false;
    this.log('UpdateService остановлен');
  }

  /**
   * Ручная проверка обновления (по кнопке/команде из UI).
   * Также оповестит соседние вкладки выполнить проверку.
   */
  public async manualCheck(): Promise<'checked' | 'unsupported'> {
    if (!this.canCheckForUpdates()) {
      this.log('ручная проверка пропущена: обновления в этом окружении выключены');
      return 'unsupported';
    }

    this.log('ручная проверка обновления…');
    await this.safeCheckForUpdate();
    this.postToOthers({ type: 'check-now' });
    return 'checked';
  }

  /**
   * Мягкий путь из UI: пользователь нажал "Обновить сейчас".
   * Эквивалент прямого вызова forceUpdate().
   */
  public confirmUpdateNow(): void {
    this.log('подтверждение обновления из UI');
    this.forceUpdate();
  }

  // -----------------------------------------------------------------------------
  // Внутренняя логика
  // -----------------------------------------------------------------------------

  /**
   * Жёсткое обновление:
   *  - Активируем новую версию SW (activateUpdate).
   *  - Выдерживаем минимальное время отображения спиннера.
   *  - Ждём смены контроллера (controllerchange) и затем перезагружаем вкладку.
   *  - На всякий случай через 2 секунды выполняем принудительный reload, если
   *    controllerchange не наступил (редкая защита от крайних случаев).
   *  - Оповещаем другие вкладки выполнить перезагрузку (BroadcastChannel).
   */
  private async forceUpdate(): Promise<void> {
    if (!this.hasBrowser || !this.swUpdate || !this.swUpdate.isEnabled) {
      this.log('forceUpdate() пропущен: среда не готова');
      return;
    }

    this.clearPendingAutoUpdate();
    this.autoUpdateScheduled = false;
    this.isUpdating.set(true);
    this.overlayVisible.set(true);
    this.overlayTitleKey.set('app-update.title');
    this.overlayTextKey.set('app-update.reloading');
    this.updateAvailable.set(false);
    this.log('активация новой версии Service Worker…');

    try {
      const tasks: Promise<unknown>[] = [this.swUpdate.activateUpdate()];
      tasks.push(this.delay(this.config.minSpinnerMs));
      if (this.config.forceDelayMs > 0) tasks.push(this.delay(this.config.forceDelayMs));

      await Promise.all(tasks);

      // Оповестим другие вкладки, чтобы они тоже перезагрузились и не остались старыми
      this.postToOthers({ type: 'force-reload' });

      // Фоллбэк — если контроллер не сменился, через 2 сек. делаем жёсткий reload
      setTimeout(() => {
        if (this.isUpdating()) {
          this.warn('controllerchange не произошёл — принудительная перезагрузка');
          this.reloadHard();
        }
      }, 2000);
    } catch (err) {
      this.error('ошибка activateUpdate:', err);
      this.reloadHard(); // попытка чистого старта
    }
  }

  /**
   * Безопасная обёртка над SwUpdate.checkForUpdate() с логами/обработкой ошибок.
   * При наличии обновления Angular сам сгенерирует событие VERSION_READY.
   */
  private async safeCheckForUpdate(): Promise<void> {
    try {
      this.log('запрос checkForUpdate()…');
      const hadUpdate = await this.swUpdate!.checkForUpdate();
      this.log('результат checkForUpdate:', hadUpdate);
    } catch (err) {
      this.error('ошибка checkForUpdate:', err);
    }
  }

  private scheduleAutoUpdate(): void {
    if (!this.hasBrowser || !this.swUpdate || !this.swUpdate.isEnabled) return;
    if (this.isUpdating() || this.autoUpdateScheduled) {
      this.log('автообновление уже выполняется или запланировано');
      return;
    }

    this.autoUpdateScheduled = true;
    this.overlayVisible.set(true);
    this.overlayTitleKey.set('app-update.title');
    this.overlayTextKey.set(this.isCriticalRoute() ? 'app-update.text-critical' : 'app-update.text');

    const delayMs = this.getAutoUpdateDelayMs();
    this.log('автообновление запланировано через', delayMs, 'мс');

    if (delayMs <= 0) {
      void this.forceUpdate();
      return;
    }

    this.pendingAutoUpdateTimeoutId = window.setTimeout(() => {
      this.pendingAutoUpdateTimeoutId = null;
      void this.forceUpdate();
    }, delayMs);
  }

  private handleCrossTabReload(): void {
    if (!this.hasBrowser) {
      this.reloadHard();
      return;
    }

    if (this.isUpdating()) return;

    this.clearPendingAutoUpdate();
    this.autoUpdateScheduled = true;
    this.overlayVisible.set(true);
    this.overlayTitleKey.set('app-update.title');
    this.overlayTextKey.set('app-update.reloading');

    this.pendingAutoUpdateTimeoutId = window.setTimeout(() => {
      this.pendingAutoUpdateTimeoutId = null;
      this.reloadHard();
    }, this.config.crossTabReloadDelayMs);
  }

  /** Обработчик: вкладка стала активной — выполняем дешёвую проверку. */
  private onVisibilityChange = () => {
    if (document.visibilityState === 'visible') {
      this.log('вкладка активна → выполняем проверку обновления');
      this.safeCheckForUpdate().catch((e) => this.error('ошибка проверки по visibility:', e));
    }
  };

  private getAutoUpdateDelayMs(): number {
    if (!this.hasBrowser) return 0;
    if (document.visibilityState !== 'visible') return 0;
    return this.isCriticalRoute() ? this.config.criticalRouteDelayMs : this.config.normalRouteDelayMs;
  }

  private isCriticalRoute(): boolean {
    if (!this.hasBrowser) return false;
    const path = this.browserWindow.locationPathname();
    return this.criticalRoutePrefixes.some((prefix) => path.startsWith(prefix));
  }

  /**
   * Жёсткая перезагрузка текущей страницы с кэш-бастингом (?_sw=timestamp),
   * чтобы минимизировать шанс попадания в промежуточное кэш-состояние.
   */
  private reloadHard(): void {
    this.isUpdating.set(false); // дефензивно снимаем флаг

    try {
      const url = new URL(this.browserWindow.locationHref());
      url.searchParams.set('_sw', String(Date.now()));
      this.warn('перезагрузка страницы (cache-busting)…');
      this.browserWindow.replaceLocation(url.toString());
    } catch (e) {
      this.warn('не удалось заменить URL, fallback на location.reload()', e);
      this.browserWindow.reloadLocation();
    }
  }

  /** Неблокирующая задержка. */
  private delay(ms: number): Promise<void> {
    return ms > 0 ? new Promise(res => setTimeout(res, ms)) : Promise.resolve();
  }

  private clearPendingAutoUpdate(): void {
    if (this.pendingAutoUpdateTimeoutId === null) return;
    clearTimeout(this.pendingAutoUpdateTimeoutId);
    this.pendingAutoUpdateTimeoutId = null;
  }

  /** Отправка сообщения в BroadcastChannel с защитой и логами. */
  private postToOthers(msg: WhSwUpdateMsg): void {
    try {
      this.channel?.postMessage(msg);
      this.log('отправлено в канал:', msg);
    } catch (e) {
      this.warn('не удалось отправить сообщение в канал', e);
    }
  }
}
